import React, { useState, useRef, useEffect } from 'react';
import {
    Text,
    Box,
    Loader,
    ActionIcon,
    Tooltip,
    Group,
    Stack,
} from '@mantine/core';
import { Card, Button, Badge } from '@xplatform/design-system';
import {
    IconChevronLeft,
    IconChevronRight,
    IconZoomIn,
    IconZoomOut,
    IconFocusCentered,
    IconScan,
    IconFileText,
} from '@tabler/icons-react';
import './ImageViewer.scss';
import { useExtraction } from '@src/hooks/useContext';
import { useTranslation } from '@hooks/useTranslation';
import ImageDisplay from '@src/components/ImageDisplay/ImageDisplay';
import type { ExtractionOptions } from '@src/hooks/extraction/useExtraction';

interface ImageViewerProps {
    uploadedFile?: File;
    extractionOptions: ExtractionOptions;
    onFileUpload: (file: File | undefined) => void;
    onOptionsChange: (options: ExtractionOptions) => void;
}

const ImageViewer: React.FC<ImageViewerProps> = ({
    uploadedFile,
    extractionOptions,
    onFileUpload,
    onOptionsChange
}) => {
    const { t } = useTranslation();
    const {
        selectPage,
        startExtraction,
        doctypeCode,
        selectedNode,
        currentPage,
        totalPages,
        extractedResult,
        isLoadingResult,
        isExtracting
    } = useExtraction();

    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });

    const imageContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, [extractedResult?.previewUrl, currentPage]);

    const handlePreviousPage = () => {
        const newPage = currentPage - 1;
        if (newPage >= 1) {
            selectPage(newPage);
        }
    };

    const handleNextPage = () => {
        const newPage = currentPage + 1;
        if (newPage <= totalPages) {
            selectPage(newPage);
        }
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev * 1.2, 5));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev / 1.2, 0.1));
    };

    const handleResetZoom = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    const handleWheel = (e: React.WheelEvent) => {
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoom(prev => Math.min(Math.max(prev * delta, 0.1), 5));
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        setLastPanPoint({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging) {
            setPan({
                x: e.clientX - lastPanPoint.x,
                y: e.clientY - lastPanPoint.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const isDisplayPagination = totalPages > 1;

    const isButtonDisabled = !uploadedFile ||
        isExtracting ||
        !doctypeCode;

    const getTooltipMessage = () => {
        if (!uploadedFile) return t('extraction.imageViewer.tooltips.uploadFileFirst');
        if (!doctypeCode) return t('extraction.imageViewer.tooltips.selectDoctypeFirst');
        if (isExtracting) return t('extraction.imageViewer.tooltips.extractionInProgress');
        return '';
    };

    return (
        <Card className="image-viewer">
            <Stack p="md" h="100%">
                <Group justify="space-between">
                    <Group gap="sm">
                        <Tooltip label={getTooltipMessage()} disabled={!getTooltipMessage()}>
                            <span>
                                <Button
                                    size="xs"
                                    onClick={async () => {
                                        if (uploadedFile && doctypeCode) {
                                            await startExtraction(uploadedFile, extractionOptions);
                                            onFileUpload(undefined);
                                            onOptionsChange({});
                                        }
                                    }}
                                    disabled={isButtonDisabled}
                                    leftSection={isExtracting ? <Loader size={16} /> : <IconScan size={16} />}
                                >
                                    {isExtracting ? t('extraction.imageViewer.buttons.processing') : t('extraction.imageViewer.buttons.runExtraction')}
                                </Button>
                            </span>
                        </Tooltip>
                    </Group>
                    <Group gap="xs">
                        <Group gap={2} mr="xs">
                            <Tooltip label={t('extraction.imageViewer.tooltips.zoomOut')}>
                                <ActionIcon variant="outline" size="sm" onClick={handleZoomOut} disabled={zoom <= 0.1}>
                                    <IconZoomOut size={16} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('extraction.imageViewer.tooltips.resetZoom')}>
                                <ActionIcon variant="outline" size="sm" onClick={handleResetZoom}>
                                    <IconFocusCentered size={16} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('extraction.imageViewer.tooltips.zoomIn')}>
                                <ActionIcon variant="outline" size="sm" onClick={handleZoomIn} disabled={zoom >= 5}>
                                    <IconZoomIn size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>

                        {isDisplayPagination && (
                            <Group gap={2}>
                                <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={handlePreviousPage}
                                    disabled={currentPage <= 1 || isLoadingResult}
                                    leftSection={<IconChevronLeft size={16} />}
                                >
                                    {t('extraction.imageViewer.buttons.previous')}
                                </Button>
                                <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={handleNextPage}
                                    disabled={currentPage >= totalPages || isLoadingResult}
                                    rightSection={<IconChevronRight size={16} />}
                                >
                                    {t('extraction.imageViewer.buttons.next')}
                                </Button>
                            </Group>
                        )}
                    </Group>
                </Group>
                <Box className="image-container">
                    {isLoadingResult ?
                        <Loader size={48} />
                        : <Box
                            className="image-dragger"
                            ref={imageContainerRef}
                            onWheel={handleWheel}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            style={{
                                cursor: isDragging ? 'grabbing' : 'grab',
                                overflow: 'hidden'
                            }}
                        >
                            <div className="image-wrapper"
                                style={{
                                    transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                                    transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                                    transformOrigin: 'center center',
                                }}
                            >
                                <ImageDisplay
                                    imageUrl={extractedResult?.previewUrl}
                                    isPdf={extractedResult?.isPdf}
                                    currentPage={currentPage}
                                    selectedNode={selectedNode}
                                />
                            </div>
                        </Box>
                    }
                </Box>
                {isDisplayPagination && (
                    <Group justify="flex-end" gap="xs" wrap="nowrap">
                        {selectedNode?.data?.originalPage ? (
                            <Tooltip
                                label={t('extraction.imageViewer.pagination.originalPageTooltip')}
                                position="top"
                                withArrow
                            >
                                <Badge
                                    variant="filled"
                                    color="brand"
                                    size="md"
                                    radius="sm"
                                    leftSection={<IconFileText size={14} />}
                                >
                                    {t('extraction.imageViewer.pagination.originalPage', {
                                        page: selectedNode.data.originalPage,
                                    })}
                                </Badge>
                            </Tooltip>
                        ) : null}
                        <Text component="span" size="sm" c="dimmed">
                            {t('extraction.imageViewer.pagination.pageInfo', { current: currentPage, total: totalPages })}
                        </Text>
                    </Group>
                )}
            </Stack>
        </Card>
    );
};

export default ImageViewer;
